import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
   Box,
   Container,
   Typography,
   Paper,
   Button,
   Grid,
   Card,
   CardContent,
   CardActions,
   CardMedia,
   Chip,
   Divider,
   CircularProgress,
   List,
   ListItem,
   ListItemIcon,
   ListItemText,
   Stepper,
   Step,
   StepLabel,
   TextField,
   Radio,
   RadioGroup,
   FormControlLabel,
   FormControl,
   FormLabel,
   Alert,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import EmailIcon from '@mui/icons-material/Email';
import LoyaltyIcon from '@mui/icons-material/Loyalty';
import PaymentIcon from '@mui/icons-material/Payment';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import {
   doc,
   getDoc,
   collection,
   query,
   where,
   getDocs,
   addDoc,
   Timestamp,
   setDoc,
   increment,
} from 'firebase/firestore';
import { db, auth } from '../firebase';
import { onAuthStateChanged, signInAnonymously } from 'firebase/auth';

// Interface for subscription tier
interface SubscriptionTier {
   id: string;
   name: string;
   monthlyPrice: number;
   annualPrice: number;
   description: string;
   benefits: {
      earlyAccess?: boolean;
      discounts?: boolean;
      privateEvents?: boolean;
      exclusiveContent?: boolean;
      prioritySupport?: boolean;
      customBenefit1?: string;
      customBenefit2?: string;
   };
   memberLimit?: number;
   unlimitedMembers?: boolean;
   active: boolean;
}

// Interface for organization
interface Organization {
   id: string;
   name: string;
   description?: string;
   logoUrl?: string;
   coverUrl?: string;
}

// Main component
const PublicSubscriptionPage: React.FC = () => {
   const { orgId } = useParams<{ orgId: string }>();
   const navigate = useNavigate();

   // State
   const [organization, setOrganization] = useState<Organization | null>(null);
   const [subscriptionTiers, setSubscriptionTiers] = useState<SubscriptionTier[]>([]);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState<string | null>(null);
   const [selectedTier, setSelectedTier] = useState<SubscriptionTier | null>(null);
   const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
   const [activeStep, setActiveStep] = useState(0);
   const [subscriberInfo, setSubscriberInfo] = useState({
      name: '',
      email: '',
      cardNumber: '',
      cardExpiry: '',
      cardCVC: '',
      billingAddress: '',
   });
   const [processing, setProcessing] = useState(false);
   const [subscriptionComplete, setSubscriptionComplete] = useState(false);
   const [userId, setUserId] = useState<string | null>(null);

   // Steps for checkout process
   const steps = ['Select Plan', 'Your Information', 'Payment', 'Confirmation'];

   // Fetch organization and subscription tiers
   useEffect(() => {
      const fetchData = async () => {
         if (!orgId) {
            setError('Organization not found');
            setLoading(false);
            return;
         }

         try {
            // Track page visit
            const linkRef = doc(db, `organizations/${orgId}/settings/publicMembershipPage`);
            const linkDoc = await getDoc(linkRef);

            if (linkDoc.exists()) {
               await setDoc(
                  linkRef,
                  {
                     visits: increment(1),
                  },
                  { merge: true }
               );
            }

            // Get organization details
            const orgRef = doc(db, `organizations/${orgId}`);
            const orgDoc = await getDoc(orgRef);

            if (!orgDoc.exists()) {
               setError('Organization not found');
               setLoading(false);
               return;
            }

            const orgData = orgDoc.data();
            setOrganization({
               id: orgDoc.id,
               name: orgData.name,
               description: orgData.description,
               logoUrl: orgData.logoUrl,
               coverUrl: orgData.coverUrl,
            });

            // Get subscription tiers
            const tiersRef = collection(db, `organizations/${orgId}/subscriptionTiers`);
            const tiersQuery = query(tiersRef, where('active', '==', true));
            const tiersSnapshot = await getDocs(tiersQuery);

            if (tiersSnapshot.empty) {
               setError('No subscription plans available');
               setLoading(false);
               return;
            }

            const tiers: SubscriptionTier[] = tiersSnapshot.docs.map(
               (doc) =>
                  ({
                     id: doc.id,
                     ...doc.data(),
                  }) as SubscriptionTier
            );

            setSubscriptionTiers(tiers);
            setLoading(false);
         } catch (err) {
            console.error('Error fetching data:', err);
            setError('Failed to load subscription information');
            setLoading(false);
         }
      };

      // Ensure user is authenticated (even anonymously)
      onAuthStateChanged(auth, (user) => {
         if (user) {
            setUserId(user.uid);
         } else {
            // Sign in anonymously if no user
            signInAnonymously(auth)
               .then((result) => {
                  setUserId(result.user.uid);
               })
               .catch((error) => {
                  console.error('Error signing in anonymously:', error);
               });
         }
      });

      fetchData();

      // Record page view
      const recordPageView = async () => {
         if (orgId) {
            try {
               const settingsRef = doc(db, `organizations/${orgId}/settings/publicMembershipPage`);
               await setDoc(
                  settingsRef,
                  {
                     visits: increment(1),
                  },
                  { merge: true }
               );
            } catch (err) {
               console.error('Error recording page view:', err);
            }
         }
      };

      recordPageView();
   }, [orgId]);

   // Handle tier selection
   const handleSelectTier = (tier: SubscriptionTier) => {
      setSelectedTier(tier);
      setActiveStep(1);
      window.scrollTo(0, 0);
   };

   // Handle billing cycle change
   const handleBillingCycleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setBillingCycle(event.target.value as 'monthly' | 'annual');
   };

   // Handle input change
   const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      setSubscriberInfo((prev) => ({
         ...prev,
         [name]: value,
      }));
   };

   // Handle next step
   const handleNext = () => {
      setActiveStep((prevStep) => prevStep + 1);
      window.scrollTo(0, 0);
   };

   // Handle back step
   const handleBack = () => {
      setActiveStep((prevStep) => prevStep - 1);
      window.scrollTo(0, 0);
   };

   // Process subscription
   const handleSubscribe = async () => {
      if (!selectedTier || !organization || !userId) return;

      setProcessing(true);

      try {
         // In a real app, you would process payment here

         // Create subscriber record
         const now = Timestamp.now();
         const nextMonth = new Date();
         nextMonth.setMonth(nextMonth.getMonth() + 1);

         const amount =
            billingCycle === 'monthly' ? selectedTier.monthlyPrice : selectedTier.annualPrice;

         const subscriberData = {
            userId: userId,
            userName: subscriberInfo.name,
            userEmail: subscriberInfo.email,
            tierId: selectedTier.id,
            tierName: selectedTier.name,
            subscribedDate: now,
            renewalDate: Timestamp.fromDate(nextMonth),
            paymentStatus: 'active',
            isAnnual: billingCycle === 'annual',
            amount: amount,
         };

         // Add to subscribers collection
         const subscribersRef = collection(db, `organizations/${orgId}/subscribers`);
         await addDoc(subscribersRef, subscriberData);

         // Track conversion
         const linkRef = doc(db, `organizations/${orgId}/settings/publicMembershipPage`);
         await setDoc(
            linkRef,
            {
               conversions: increment(1),
            },
            { merge: true }
         );

         // Show confirmation
         setSubscriptionComplete(true);
         setActiveStep(3);
      } catch (err) {
         console.error('Error processing subscription:', err);
         setError('Failed to process subscription. Please try again.');
      } finally {
         setProcessing(false);
      }
   };

   // Render tier benefits
   const renderBenefits = (tier: SubscriptionTier) => {
      const benefits = [];

      if (tier.benefits.earlyAccess) {
         benefits.push('Early Access to Events');
      }
      if (tier.benefits.discounts) {
         benefits.push('Exclusive Discounts');
      }
      if (tier.benefits.privateEvents) {
         benefits.push('Private Events Access');
      }
      if (tier.benefits.exclusiveContent) {
         benefits.push('Exclusive Content');
      }
      if (tier.benefits.prioritySupport) {
         benefits.push('Priority Support');
      }
      if (tier.benefits.customBenefit1) {
         benefits.push(tier.benefits.customBenefit1);
      }
      if (tier.benefits.customBenefit2) {
         benefits.push(tier.benefits.customBenefit2);
      }

      return (
         <List dense disablePadding>
            {benefits.map((benefit, index) => (
               <ListItem key={index} disablePadding sx={{ pb: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 30 }}>
                     <CheckCircleOutlineIcon color='primary' fontSize='small' />
                  </ListItemIcon>
                  <ListItemText primary={benefit} />
               </ListItem>
            ))}
         </List>
      );
   };

   // Render step content
   const getStepContent = (step: number) => {
      switch (step) {
         case 0:
            return (
               <Box sx={{ mt: 4 }}>
                  <Box sx={{ mb: 4 }}>
                     <FormControl component='fieldset'>
                        <FormLabel component='legend'>Billing Cycle</FormLabel>
                        <RadioGroup row value={billingCycle} onChange={handleBillingCycleChange}>
                           <FormControlLabel value='monthly' control={<Radio />} label='Monthly' />
                           <FormControlLabel
                              value='annual'
                              control={<Radio />}
                              label='Annual (Save up to 20%)'
                           />
                        </RadioGroup>
                     </FormControl>
                  </Box>

                  <Grid container spacing={3}>
                     {subscriptionTiers.map((tier) => (
                        <Grid item xs={12} md={4} key={tier.id}>
                           <Card
                              sx={{
                                 height: '100%',
                                 display: 'flex',
                                 flexDirection: 'column',
                                 borderColor:
                                    selectedTier?.id === tier.id ? 'primary.main' : 'transparent',
                                 borderWidth: selectedTier?.id === tier.id ? 2 : 1,
                                 borderStyle: 'solid',
                              }}
                           >
                              <CardContent sx={{ flexGrow: 1 }}>
                                 <Typography variant='h5' component='h2' gutterBottom>
                                    {tier.name}
                                 </Typography>

                                 <Typography variant='h4' component='div' sx={{ mb: 2 }}>
                                    $
                                    {billingCycle === 'monthly'
                                       ? tier.monthlyPrice
                                       : tier.annualPrice}
                                    <Typography variant='caption' color='text.secondary'>
                                       /{billingCycle === 'monthly' ? 'month' : 'year'}
                                    </Typography>
                                 </Typography>

                                 {billingCycle === 'annual' &&
                                    tier.monthlyPrice * 12 > tier.annualPrice && (
                                       <Chip
                                          label={`Save $${(tier.monthlyPrice * 12 - tier.annualPrice).toFixed(2)}`}
                                          color='success'
                                          size='small'
                                          sx={{ mb: 2 }}
                                       />
                                    )}

                                 <Typography variant='body2' color='text.secondary' paragraph>
                                    {tier.description}
                                 </Typography>

                                 <Divider sx={{ my: 2 }} />

                                 {renderBenefits(tier)}
                              </CardContent>
                              <CardActions>
                                 <Button
                                    fullWidth
                                    variant='contained'
                                    color='primary'
                                    onClick={() => handleSelectTier(tier)}
                                 >
                                    Select Plan
                                 </Button>
                              </CardActions>
                           </Card>
                        </Grid>
                     ))}
                  </Grid>
               </Box>
            );
         case 1:
            return (
               <Paper sx={{ p: 3, mt: 3 }}>
                  <Typography variant='h6' component='h2' gutterBottom>
                     Your Information
                  </Typography>

                  <Grid container spacing={3}>
                     <Grid item xs={12}>
                        <TextField
                           required
                           fullWidth
                           label='Full Name'
                           name='name'
                           value={subscriberInfo.name}
                           onChange={handleInputChange}
                           InputProps={{
                              startAdornment: <AccountCircleIcon color='action' sx={{ mr: 1 }} />,
                           }}
                        />
                     </Grid>
                     <Grid item xs={12}>
                        <TextField
                           required
                           fullWidth
                           label='Email Address'
                           name='email'
                           type='email'
                           value={subscriberInfo.email}
                           onChange={handleInputChange}
                           InputProps={{
                              startAdornment: <EmailIcon color='action' sx={{ mr: 1 }} />,
                           }}
                        />
                     </Grid>
                  </Grid>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                     <Button onClick={handleBack}>Back</Button>
                     <Button
                        variant='contained'
                        color='primary'
                        onClick={handleNext}
                        disabled={!subscriberInfo.name || !subscriberInfo.email}
                     >
                        Next
                     </Button>
                  </Box>
               </Paper>
            );
         case 2:
            return (
               <Paper sx={{ p: 3, mt: 3 }}>
                  <Typography variant='h6' component='h2' gutterBottom>
                     Payment Information
                  </Typography>

                  <Alert severity='info' sx={{ mb: 3 }}>
                     This is a demo. No actual payment will be processed.
                  </Alert>

                  <Grid container spacing={3}>
                     <Grid item xs={12}>
                        <TextField
                           required
                           fullWidth
                           label='Card Number'
                           name='cardNumber'
                           placeholder='4242 4242 4242 4242'
                           value={subscriberInfo.cardNumber}
                           onChange={handleInputChange}
                           InputProps={{
                              startAdornment: <CreditCardIcon color='action' sx={{ mr: 1 }} />,
                           }}
                        />
                     </Grid>
                     <Grid item xs={6}>
                        <TextField
                           required
                           fullWidth
                           label='Expiry Date'
                           name='cardExpiry'
                           placeholder='MM/YY'
                           value={subscriberInfo.cardExpiry}
                           onChange={handleInputChange}
                        />
                     </Grid>
                     <Grid item xs={6}>
                        <TextField
                           required
                           fullWidth
                           label='CVC'
                           name='cardCVC'
                           placeholder='123'
                           value={subscriberInfo.cardCVC}
                           onChange={handleInputChange}
                        />
                     </Grid>
                     <Grid item xs={12}>
                        <TextField
                           fullWidth
                           label='Billing Address'
                           name='billingAddress'
                           value={subscriberInfo.billingAddress}
                           onChange={handleInputChange}
                        />
                     </Grid>
                  </Grid>

                  <Box sx={{ mt: 4, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                     <Typography variant='subtitle1' gutterBottom>
                        Order Summary
                     </Typography>
                     <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant='body2'>
                           {selectedTier?.name} ({billingCycle})
                        </Typography>
                        <Typography variant='body2'>
                           $
                           {billingCycle === 'monthly'
                              ? selectedTier?.monthlyPrice
                              : selectedTier?.annualPrice}
                        </Typography>
                     </Box>
                     <Divider sx={{ my: 1 }} />
                     <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant='subtitle2'>Total</Typography>
                        <Typography variant='subtitle2'>
                           $
                           {billingCycle === 'monthly'
                              ? selectedTier?.monthlyPrice
                              : selectedTier?.annualPrice}
                        </Typography>
                     </Box>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                     <Button onClick={handleBack}>Back</Button>
                     <Button
                        variant='contained'
                        color='primary'
                        onClick={handleSubscribe}
                        disabled={
                           processing ||
                           !subscriberInfo.cardNumber ||
                           !subscriberInfo.cardExpiry ||
                           !subscriberInfo.cardCVC
                        }
                     >
                        {processing ? <CircularProgress size={24} /> : 'Subscribe Now'}
                     </Button>
                  </Box>
               </Paper>
            );
         case 3:
            return (
               <Paper sx={{ p: 4, mt: 3, textAlign: 'center' }}>
                  <CheckCircleOutlineIcon color='success' sx={{ fontSize: 60, mb: 2 }} />
                  <Typography variant='h4' component='h2' gutterBottom>
                     Subscription Confirmed!
                  </Typography>
                  <Typography variant='body1' paragraph>
                     Thank you for subscribing to {organization?.name}'s {selectedTier?.name} plan.
                  </Typography>
                  <Typography variant='body2' color='text.secondary' paragraph>
                     A confirmation email has been sent to {subscriberInfo.email}.
                  </Typography>
                  <Button
                     variant='contained'
                     color='primary'
                     onClick={() => navigate('/dashboard')}
                     sx={{ mt: 2 }}
                  >
                     Return Home
                  </Button>
               </Paper>
            );
         default:
            return <Typography>Unknown step</Typography>;
      }
   };

   // Show loading state
   if (loading) {
      return (
         <Container maxWidth='lg'>
            <Box
               sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: '50vh',
               }}
            >
               <CircularProgress />
            </Box>
         </Container>
      );
   }

   // Show error message
   if (error) {
      return (
         <Container maxWidth='lg'>
            <Paper sx={{ p: 4, mt: 3, textAlign: 'center' }}>
               <Typography variant='h5' color='error' gutterBottom>
                  {error}
               </Typography>
               <Button variant='contained' onClick={() => navigate('/dashboard')} sx={{ mt: 2 }}>
                  Return Home
               </Button>
            </Paper>
         </Container>
      );
   }

   return (
      <Container maxWidth='lg'>
         {/* Organization header */}
         <Paper
            sx={{
               p: 3,
               mt: 3,
               mb: 4,
               backgroundImage: organization?.coverUrl
                  ? `url(${organization.coverUrl})`
                  : undefined,
               backgroundSize: 'cover',
               backgroundPosition: 'center',
               position: 'relative',
               '&::before': organization?.coverUrl
                  ? {
                       content: '""',
                       position: 'absolute',
                       top: 0,
                       left: 0,
                       right: 0,
                       bottom: 0,
                       backgroundColor: 'rgba(0, 0, 0, 0.6)',
                       zIndex: 1,
                    }
                  : undefined,
            }}
         >
            <Box
               sx={{
                  position: 'relative',
                  zIndex: 2,
                  color: organization?.coverUrl ? 'white' : 'inherit',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 3,
               }}
            >
               {organization?.logoUrl && (
                  <Box
                     component='img'
                     src={organization.logoUrl}
                     alt={organization.name}
                     sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        objectFit: 'cover',
                        border: '2px solid white',
                     }}
                  />
               )}
               <Box>
                  <Typography variant='h4' component='h1' gutterBottom>
                     {organization?.name} Membership
                  </Typography>
                  {organization?.description && (
                     <Typography variant='body1'>{organization.description}</Typography>
                  )}
               </Box>
            </Box>
         </Paper>

         {/* Stepper */}
         <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
               <Step key={label}>
                  <StepLabel>{label}</StepLabel>
               </Step>
            ))}
         </Stepper>

         {/* Step content */}
         {getStepContent(activeStep)}
      </Container>
   );
};

export default PublicSubscriptionPage;
